import { forwardRef, type KeyboardEvent } from "react";
import { AlertTriangle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  buildMyLibraryReturnTo,
  deriveLibraryTitle,
  getLibraryItemStatusLabel,
  getLibraryProviderLabel,
} from "@/lib/library";
import { cn } from "@/lib/utils";
import type { ImportDraft } from "./libraryImportParsing";
import { getDraftTestId } from "./libraryImportParsing";

type Props = {
  draft: ImportDraft;
  draftKey: string;
  highlighted: boolean;
  onFocusValidity: (validity: "valid" | "invalid" | null) => void;
  onKeyDown: (event: KeyboardEvent<HTMLDivElement>) => void;
  onOpenExisting: (itemId: string) => void;
  myLibraryReturnTo?: string;
};

export const LibraryImportDraftRow = forwardRef<HTMLDivElement, Props>(
  ({ draft, draftKey, highlighted, onFocusValidity, onKeyDown, onOpenExisting, myLibraryReturnTo }, ref) => (
    <div
      key={draftKey}
      ref={ref}
      tabIndex={draft.validationIssues.length ? 0 : -1}
      data-review-row-validity={draft.validationIssues.length ? "invalid" : "valid"}
      data-highlighted={highlighted}
      data-testid={getDraftTestId(draft)}
      aria-label={highlighted ? `Highlighted review row ${draft.title}` : undefined}
      onFocus={() => onFocusValidity(draft.validationIssues.length ? "invalid" : "valid")}
      onBlur={() => onFocusValidity(null)}
      onKeyDown={onKeyDown}
      className={cn(
        "rounded-md border border-border bg-muted/20 p-3 transition-shadow focus:outline-none",
        highlighted
          ? "border-primary/50 bg-accent/20 ring-2 ring-primary/40 ring-offset-2 ring-offset-background shadow-sm focus:ring-4 focus:ring-primary/60"
          : undefined,
      )}
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="font-medium text-foreground">{draft.title}</p>
          <p className="text-sm text-muted-foreground">{draft.authorText || draft.importLabel}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Badge variant="secondary">{getLibraryItemStatusLabel(draft.status)}</Badge>
          <Badge variant="outline">{getLibraryProviderLabel(draft.sourceProvider)}</Badge>
          {draft.validationIssues.length ? (
            <Badge variant="outline" className="gap-1">
              <AlertTriangle className="h-3 w-3" /> {draft.validationIssues[0]}
            </Badge>
          ) : null}
          {draft.duplicateWarnings.length ? (
            <Badge variant="outline" className="gap-1">
              <AlertTriangle className="h-3 w-3" /> {draft.duplicateWarnings.length} possible duplicate
              {draft.duplicateWarnings.length === 1 ? "" : "s"}
            </Badge>
          ) : null}
        </div>
      </div>
      <div className="mt-2 flex flex-wrap gap-2 text-xs text-muted-foreground">
        {draft.publishedDate ? <span>{draft.publishedDate}</span> : null}
        {draft.isbn13 ? (
          <span>ISBN-13 {draft.isbn13}</span>
        ) : draft.isbn10 ? (
          <span>ISBN-10 {draft.isbn10}</span>
        ) : null}
        {draft.publisher ? <span>{draft.publisher}</span> : null}
      </div>
      {draft.validationIssues.length ? (
        <div className="mt-3 space-y-1 rounded-md border border-border bg-background/70 p-3 text-xs text-muted-foreground">
          {draft.validationIssues.map((issue) => (
            <p key={`${draft.importLabel}-${issue}`}>{issue}</p>
          ))}
        </div>
      ) : null}
      {draft.duplicateWarnings.length ? (
        <div className="mt-3 space-y-2 rounded-md border border-border bg-background/70 p-3">
          {draft.duplicateWarnings.map((warning) => (
            <div key={`${draft.importLabel}-${warning.item.id}`} className="space-y-2 text-sm">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <p className="font-medium text-foreground">{deriveLibraryTitle(warning.item)}</p>
                  <p className="text-xs text-muted-foreground">{warning.headline}</p>
                </div>
                <Badge variant={warning.severity === "strong" ? "secondary" : "outline"}>
                  {warning.classification === "same-record"
                    ? "Likely same record"
                    : warning.classification === "alternate-edition"
                      ? "Alternate edition"
                      : "Translation or volume"}
                </Badge>
              </div>
              <p className="text-xs text-muted-foreground">{warning.detail}</p>
              {warning.comparison.length ? (
                <div className="flex flex-wrap gap-2">
                  {warning.comparison.map((part) => (
                    <Badge key={part} variant="outline" className="text-[11px]">
                      {part}
                    </Badge>
                  ))}
                </div>
              ) : null}
              <div className="flex flex-wrap gap-3 text-xs">
                <button
                  type="button"
                  className="text-primary hover:underline"
                  onClick={() => onOpenExisting(warning.item.id)}
                >
                  Open existing record
                </button>
                <a
                  href={`/${myLibraryReturnTo ?? buildMyLibraryReturnTo({ itemId: warning.item.id })}`}
                  className="text-primary hover:underline"
                >
                  Open in My Library
                </a>
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  ),
);
LibraryImportDraftRow.displayName = "LibraryImportDraftRow";
